/**
 * Persistencia de carpeta raíz para exportación BNT.TaxRepository (navegador).
 */
import {
  clearStoredTaxRepoRootPrefs,
  setStoredTaxRepoRootLabel,
  setStoredTaxRepoRootReady,
  TAX_REPO_ROOT_FOLDER,
  TAX_REPO_XML_SUBFOLDER,
} from "@/lib/exportTaxRepositoryPaths";
import { pickExportDirectory, supportsDirectoryPicker, verifyWritableDirectory, folderPickerBlockedHint } from "@/lib/cfdiExportFolder";
import { ensureTaxRepositoryRootDirectory, isPickerUserAbort } from "@/lib/exportTaxRepositoryFs";

const IDB_NAME = "visor_export_directorio";
const IDB_STORE = "handles";
const IDB_KEY_TAX_REPO_ROOT = "tax_repo_root_dir";

function openIdb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onerror = () => reject(req.error || new Error("No se pudo abrir IndexedDB"));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function idbGet(key) {
  const db = await openIdb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readonly");
    const req = tx.objectStore(IDB_STORE).get(key);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(key, value) {
  const db = await openIdb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.objectStore(IDB_STORE).put(value, key);
  });
}

async function idbDelete(key) {
  const db = await openIdb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.objectStore(IDB_STORE).delete(key);
  });
}

async function ensureDirPermission(handle, mode = "readwrite") {
  if (!handle) return false;
  const opts = { mode };
  const q = await handle.queryPermission(opts);
  if (q === "granted") return true;
  const r = await handle.requestPermission(opts);
  return r === "granted";
}

export async function saveTaxRepoRootDirectoryHandle(handle, displayLabel) {
  if (!handle) throw new Error("Handle de carpeta inválido");
  await idbSet(IDB_KEY_TAX_REPO_ROOT, handle);
  const label = displayLabel || `${handle.name || "Carpeta"}/${TAX_REPO_ROOT_FOLDER}`;
  setStoredTaxRepoRootLabel(label);
  setStoredTaxRepoRootReady(true);
}

/** Crea BNT.TaxRepository y, si hay RFC, la carpeta base del cliente. */
export async function bootstrapTaxRepositoryFolder(userRootHandle, clientRfc) {
  const taxRoot = await ensureTaxRepositoryRootDirectory(userRootHandle);
  const rfc = String(clientRfc || "").trim().toUpperCase().replace(/[^A-Z0-9&Ñ]/g, "");
  if (rfc.length >= 10) {
    const rfcDir = await taxRoot.getDirectoryHandle(rfc, { create: true });
    await rfcDir.getDirectoryHandle(TAX_REPO_XML_SUBFOLDER, { create: true });
  }
  return taxRoot;
}

export function formatTaxRepoDisplayPath(parentLabel) {
  const base = String(parentLabel || "Carpeta").trim() || "Carpeta";
  if (base.includes(TAX_REPO_ROOT_FOLDER)) return base;
  return `${base}/${TAX_REPO_ROOT_FOLDER}`;
}

/**
 * Comprueba si la carpeta guardada sigue accesible (permisos + escritura).
 * @returns {{ ok: boolean, lostAccess: boolean, handle: FileSystemDirectoryHandle|null }}
 */
export async function validateStoredTaxRepoRootAccess() {
  const handle = await loadTaxRepoRootDirectoryHandle();
  if (!handle) {
    return { ok: false, lostAccess: false, handle: null };
  }
  try {
    const writable = await verifyWritableDirectory(handle);
    if (!writable) {
      return { ok: false, lostAccess: true, handle: null };
    }
    await bootstrapTaxRepositoryFolder(handle, "");
    return { ok: true, lostAccess: false, handle };
  } catch {
    return { ok: false, lostAccess: true, handle: null };
  }
}

export async function loadTaxRepoRootDirectoryHandle() {
  try {
    return await idbGet(IDB_KEY_TAX_REPO_ROOT);
  } catch {
    return null;
  }
}

export async function clearTaxRepoRootDirectoryPreference() {
  try {
    await idbDelete(IDB_KEY_TAX_REPO_ROOT);
  } catch {
    /* ignore */
  }
  clearStoredTaxRepoRootPrefs();
}

export async function pickAndSaveTaxRepoRootDirectory(clientRfc) {
  if (!supportsDirectoryPicker()) {
    throw new Error("DIRECTORY_PICKER_UNSUPPORTED");
  }
  const { handle, pickerLabel } = await pickExportDirectory();
  try {
    await bootstrapTaxRepositoryFolder(handle, clientRfc);
  } catch (e) {
    throw new Error(
      `Se eligió la carpeta pero no se pudo crear ${TAX_REPO_ROOT_FOLDER}: ${e?.message || e}. ${folderPickerBlockedHint()}`,
    );
  }
  const displayPath = formatTaxRepoDisplayPath(pickerLabel || handle.name);
  await saveTaxRepoRootDirectoryHandle(handle, displayPath);
  return { handle, displayPath };
}

export async function getWritableTaxRepoRootHandle() {
  const handle = await loadTaxRepoRootDirectoryHandle();
  if (!handle) return null;
  const ok = await ensureDirPermission(handle, "readwrite");
  return ok ? handle : null;
}

export { isPickerUserAbort, supportsDirectoryPicker };
